import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Check, Copy, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Logo } from "./Brand";
import { PUBLIC_URL } from "../lib/site";

const CopyRow = ({ id, label, hint, value }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      toast.success(`${label} copied`);
      setTimeout(() => setDone(false), 1600);
    } catch {
      toast.error("Copy failed — select the text and copy it manually");
    }
  };
  return (
    <div className="rounded-2xl border border-[#f4ede2]/10 bg-[#121016] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold">{label}</p>
          <p className="mt-1 text-sm text-[#c4bbb0]">{hint}</p>
        </div>
        <button data-testid={id} onClick={copy} className="shrink-0 flex items-center gap-2 rounded-full border border-[#e3ba73]/40 text-[#e3ba73] px-4 py-2 text-sm hover:bg-[#e3ba73] hover:text-[#121016] transition-colors duration-300">
          {done ? <Check size={14} /> : <Copy size={14} />}{done ? "Copied" : "Copy"}
        </button>
      </div>
      <code className="mt-4 block font-mono-d text-xs text-[#d9d0c4] break-all bg-[#1c1824] rounded-lg p-3">{value}</code>
    </div>
  );
};

export const ShareStudio = () => {
  const qrRef = useRef(null);
  const url = PUBLIC_URL;
  const html = `<a href="${url}" target="_blank" rel="noopener" style="display:inline-block;padding:12px 24px;border-radius:999px;background:#e3ba73;color:#121016;font-weight:600;text-decoration:none">Support my writing</a>`;
  const download = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "even-keel-tip-qr.png";
    a.click();
  };

  return (
    <section id="share" data-testid="embed-widget-section" className="py-28 sm:py-36 bg-[#16121b] border-t border-[#e3ba73]/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal><p className="overline mb-6">Share this page</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif-d text-4xl sm:text-5xl tracking-tight leading-[1.05]">One link for Medium, Substack & print.</h2></Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#c4bbb0] leading-relaxed">
              Medium doesn't allow payment forms inside articles, so readers tap a link and tip here. Add it at the end of a story, in your Medium tipping settings, in your Substack footer — or print the QR code.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-5 space-y-4">
          <Reveal delay={0.1}><CopyRow id="copy-link-button" label="Tipping link" hint="Paste into Medium's tipping URL setting or your bio." value={url} /></Reveal>
          <Reveal delay={0.15}><CopyRow id="copy-medium-embed-button" label="Medium sign-off" hint="Paste at the end of an article; Medium turns the URL into a link." value={`If my writing has helped you, you can support it here: ${url}`} /></Reveal>
          <Reveal delay={0.2}><CopyRow id="copy-html-button" label="HTML button" hint="For your website or newsletter tools that accept HTML." value={html} /></Reveal>
        </div>
        <Reveal delay={0.2} className="lg:col-span-3">
          <div className="rounded-3xl bg-[#f4ede2] text-[#121016] p-6 flex flex-col items-center text-center">
            <div ref={qrRef} data-testid="qr-code" className="bg-white p-3 rounded-xl">
              <QRCodeCanvas value={url} size={180} fgColor="#121016" bgColor="#ffffff" level="M" includeMargin={false} />
            </div>
            <div className="mt-5 flex items-center gap-2"><Logo size={20} /><span className="font-serif-d text-lg">Scan to support</span></div>
            <button data-testid="download-qr-code-button" onClick={download} className="mt-5 w-full flex items-center justify-center gap-2 rounded-full bg-[#121016] text-[#f4ede2] py-3 text-sm font-semibold hover:bg-[#5e253b] transition-colors duration-300">
              <Download size={15} /> Download QR (PNG)
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
